import React, { Fragment, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import {
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from '@mui/material';
import ValidatedTextField from 'components/Common/ValidatedTextField';
import { Form, Formik } from 'formik';
import { closeAllModals, openModal } from 'reducers/modalReducer';
import { formatDate } from 'utils/stringUtil';
import partnerService from 'services/partnerService';
import { HttpError } from 'components/Error/HttpError';
import ResponsiveDialog from 'components/Common/ResponsiveDialog';
import { PartnerType } from 'types/Partner';

interface GdprDataRequestHistory {
  caseId: string;
  globalPartnerId: string;
  requestTime: string;
  requestorUserId: string;
  requestorFullName: string;
}

const GdprDataRemovalModal = ({ partnerType, techId }: { partnerType: PartnerType; techId: string }) => {
  const dispatch = useDispatch();
  const [removing, setRemoving] = useState(false);
  const [confirmationChecked, setConfirmationChecked] = useState(false);
  const [gdprRequestHistory, setGdprRequestHistory] = useState<GdprDataRequestHistory[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    techId &&
      partnerService.getGdprRequestHistory(techId).then(
        (response) => {
          setGdprRequestHistory(response);
        },
        function (error: HttpError) {
          if (!error.message.includes('404')) {
            dispatch(
              openModal({
                modalType: 'JBErrorModalWrapper',
                modalProps: {
                  technicalInfo: {
                    details: 'Error when fetching the GDPR data from partner-data-mgmt',
                    title: 'Error when fetching the GDPR data',
                    userErrorMessage: 'An error occurred while fetching the GDPR data',
                  },
                  httpError: error,
                },
              }),
            );
          }
        },
      );
  }, [dispatch, techId]);

  const handleCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmationChecked(event.target.checked);
  };

  return (
    <ResponsiveDialog id="gdpr-dialog">
      <Formik
        onSubmit={() => {}}
        initialValues={{
          caseId: '',
        }}
      >
        {({ values }) => {
          return (
            <Form>
              <DialogContent style={{ minWidth: '40%' }}>
                <DialogTitle id="gdpr-dialog-title">
                  <Typography variant="h4">GDPR Data Removal Request</Typography>
                </DialogTitle>

                <Typography variant="h6" style={{ marginTop: '15px' }}>
                  Previous data requests
                </Typography>

                {gdprRequestHistory.length > 0 ? (
                  gdprRequestHistory.map((gdprRequest, index) => {
                    return (
                      <Fragment key={index}>
                        <Typography data-testid={`gdpr-history-${index}`} id={`gdpr-history-${index}`}>
                          {formatDate(gdprRequest.requestTime)} -{' '}
                          {`${gdprRequest.requestorFullName} (${gdprRequest.requestorUserId})`}
                        </Typography>
                      </Fragment>
                    );
                  })
                ) : (
                  <Typography>No GDPR data requests.</Typography>
                )}

                <Typography variant="h6" style={{ marginTop: '15px' }}>
                  Data removal
                </Typography>

                <div style={{ width: '300px' }}>
                  <ValidatedTextField
                    label="Please enter case ID"
                    disabled={removing}
                    name="caseId"
                    test-id="removal-caseId-text-input"
                    placeholder=""
                    maxChar={250}
                  />
                </div>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={confirmationChecked}
                      onChange={handleCheck}
                      id="gdpr-removal-confirm-checkbox"
                      color="primary"
                      inputProps={{ 'aria-label': 'primary checkbox' }}
                    />
                  }
                  label="Yes, I want to remove all data"
                />
              </DialogContent>
              <DialogActions style={{ padding: '5%' }}>
                <Button
                  onClick={() => dispatch(closeAllModals())}
                  autoFocus
                  color="primary"
                  id="gdpr-removal-cancel-button"
                >
                  Cancel
                </Button>
                <Button
                  disabled={removing || !values.caseId || !confirmationChecked}
                  color="primary"
                  onClick={async () => {
                    setRemoving(true);
                    await partnerService.removeGdprData(techId, values.caseId, partnerType).then(
                      () => {
                        navigate('/partners');
                        dispatch(
                          openModal({
                            modalType: 'WarningDialog',
                            modalProps: {
                              dialogOpen: true,
                              title: 'Success',
                              bodyText: 'The prospect has been successfully anonymized.',
                              affirmText: 'OK',
                              action: () => {
                                dispatch(closeAllModals());
                              },
                              isCancelNeeded: false,
                              cancelAction: () => {
                                //intentionally left blank
                              },
                            },
                          }),
                        );
                      },
                      function (error: HttpError) {
                        dispatch(
                          openModal({
                            modalType: 'JBErrorModalWrapper',
                            modalProps: {
                              technicalInfo: {
                                details: 'Error when removing the GDPR data from partner-data-mgmt',
                                title: 'Error when removing the GDPR data',
                                userErrorMessage: 'An error occurred while removing the GDPR data',
                              },
                              httpError: error,
                            },
                          }),
                        );
                      },
                    );
                  }}
                  variant="contained"
                  id="gdpr-removal-button"
                >
                  {removing ? (
                    <>
                      Removing.. <CircularProgress size={20} />
                    </>
                  ) : (
                    <>Remove data</>
                  )}
                </Button>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </ResponsiveDialog>
  );
};

export default GdprDataRemovalModal;
